import React, { useState, createElement } from 'react';
import { ClipboardCopyIcon, DownloadIcon, CheckIcon } from 'lucide-react';
import { calculateBasePrice } from './pricingUtils';
import { calculateAdderTotal } from './AdderSelector';
import { type WindowData, getCasementSurcharge, hasCasement, hasHingeInNotes, TEMPERED_GLASS_COST } from './WindowRow';
import type { DoorData } from './DoorRow';
import { type SwingDoorData, getSwingDoorOperationLabel, isSwingDoorOperationComplete } from './SwingDoorRow';
interface ExportActionsProps {
  windowItems: WindowData[];
  doorItems: DoorData[];
  swingDoorItems: SwingDoorData[];
  productLine: string;
  selectedFrameColor: string;
  selectedExteriorColor: string;
  exteriorColorCost: number;
  totalBasePrice: number;
  totalAdderCost: number;
  grandTotal: number;
  windowDiscount: number;
  sgdDiscount: number;
  swingDoorDiscount: number;
  totalDiscountAmount: number;
  totalDue: number;
  totalWindowCount: number;
  avgPerWindow: number;
}
function fmt(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
const OP_ABBR: Record<string, string> = {
  'Single Hung': 'SH',
  'Double Hung': 'DH',
  'Sliding Glass Door': 'SGD',
  Picture: 'Pic'
};
function abbrevOp(op: string): string {
  return OP_ABBR[op] ?? op;
}
function pad(str: string, width: number, alignRight = false): string {
  if (alignRight) return str.padStart(width);
  return str.padEnd(width);
}
function isSwingDoor(item: WindowData | DoorData | SwingDoorData): item is SwingDoorData {
  return 'panels' in item && 'swingDirection' in item && 'hinge' in item;
}
function isWindow(item: WindowData | DoorData | SwingDoorData): item is WindowData {
  // Windows are the only items where operations may include 'Casement', 'Single Hung', etc.
  // Doors and swing doors have their own operation types. Detect by checking it's not a swing door
  // and has the WindowData-style operations array (not a door operation).
  if (isSwingDoor(item)) return false;
  // Use operation values to distinguish: window ops include things like 'Casement', 'Single Hung', etc.
  // For simplicity, treat anything that isn't a swing door but has 'Casement' as a window.
  return Array.isArray((item as WindowData).operations);
}
function getItemAdderCost(win: WindowData | DoorData | SwingDoorData, extCost: number): number {
  let cost = calculateAdderTotal(win.adders) + extCost;
  if (!isSwingDoor(win)) {
    cost += getCasementSurcharge(win.operations as any);
    // Tempered glass only applies to windows (SGDs and swing doors are tempered by default)
    if ((win as WindowData).tempered) {
      cost += TEMPERED_GLASS_COST;
    }
  }
  return cost;
}
function getItemOperationString(win: WindowData | DoorData | SwingDoorData): string {
  if (isSwingDoor(win)) {
    return getSwingDoorOperationLabel(win);
  }
  return win.operations && win.operations.length > 0 ? win.operations.map(abbrevOp).join(', ') : '—';
}
const T = '\t';
function buildItemRowPlain(win: WindowData | DoorData | SwingDoorData, productLine: string, extCost: number): string {
  const sqIn = win.width * win.height;
  const sqFt = sqIn / 144;
  const basePrice = calculateBasePrice(win.width, win.height, productLine);
  const adderCost = getItemAdderCost(win, extCost);
  const unitPrice = basePrice + adderCost;
  const lineTotal = unitPrice * win.qty;
  const adderList = Object.entries(win.adders).filter(([, v]) => v.checked).map(([name]) => name);
  if (extCost > 0) adderList.push('Ext Color');
  if (!isSwingDoor(win) && hasCasement(win.operations as any)) adderList.push('Casement');
  if ((win as any).tempered) adderList.push('Tempered');
  const adders = adderList.length === 0 ? 'None' : adderList.length === 1 ? adderList[0].slice(0, 12) : adderList.join(', ');
  const ops = getItemOperationString(win);
  const glass = win.glass || 'No';
  const adderStr = adderCost > 0 ? fmt(adderCost) : '$0.00';
  const notes = win.notes || '';
  return `${win.qty}${T}${win.width}"×${win.height}"${T}${sqFt > 0 ? sqFt.toFixed(1) : '—'}${T}${ops}${T}${glass}${T}${adders}${T}${adderStr}${T}${fmt(lineTotal)}${T}${notes}`;
}
function buildSectionPlain(sectionLabel: string, items: (WindowData | DoorData | SwingDoorData)[], productLine: string, extCost: number): string[] {
  const included = items.filter(w => w.included);
  if (included.length === 0) return [];
  const lines: string[] = [];
  lines.push(`— ${sectionLabel} —`);
  lines.push(`Qty${T}Size${T}Sq Ft${T}Op${T}Obs${T}Add-Ons${T}Adder ${T}Total${T}Notes`);
  lines.push('');
  included.forEach(win => {
    lines.push(buildItemRowPlain(win, productLine, extCost));
  });
  lines.push('');
  return lines;
}
function buildPlainText(props: ExportActionsProps): string {
  const {
    windowItems,
    doorItems,
    swingDoorItems,
    productLine,
    selectedFrameColor,
    selectedExteriorColor,
    totalBasePrice,
    totalAdderCost,
    grandTotal,
    windowDiscount,
    sgdDiscount,
    swingDoorDiscount,
    totalDiscountAmount,
    totalDue,
    totalWindowCount
  } = props;
  const lines: string[] = [];
  const colorStr = selectedExteriorColor !== 'None' ? `${selectedFrameColor.toUpperCase()} / ${selectedExteriorColor.toUpperCase()}` : selectedFrameColor.toUpperCase();
  lines.push(`QUOTE — ${productLine.toUpperCase()} (${colorStr})`);
  lines.push(`Date: ${new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })}`);
  lines.push('');
  const extCost = props.exteriorColorCost;
  lines.push(...buildSectionPlain('WINDOWS', windowItems, productLine, extCost));
  lines.push(...buildSectionPlain('SLIDING GLASS DOORS', doorItems, productLine, extCost));
  lines.push(...buildSectionPlain('SWING DOORS', swingDoorItems, productLine, extCost));
  lines.push(`Total Items:${T}${totalWindowCount}`);
  lines.push(`Base Total:${T}${fmt(totalBasePrice)}`);
  lines.push(`Adders Total:${T}${fmt(totalAdderCost)}`);
  lines.push(`Subtotal:${T}${fmt(grandTotal)}`);
  const discountLines: string[] = [];
  if (windowDiscount > 0) discountLines.push(`Windows Discount (${windowDiscount.toFixed(1)}%)`);
  if (sgdDiscount > 0) discountLines.push(`SGD Discount (${sgdDiscount.toFixed(1)}%)`);
  if (swingDoorDiscount > 0) discountLines.push(`Swing Door Discount (${swingDoorDiscount.toFixed(1)}%)`);
  if (totalDiscountAmount > 0) {
    lines.push(`Discount:${T}−${fmt(totalDiscountAmount)}`);
    if (discountLines.length > 0) {
      lines.push(`${T}${discountLines.join(', ')}`);
    }
  }
  lines.push(`Total Due:${T}${fmt(totalDue)}`);
  return lines.join('\n');
}
function buildItemRowCsv(win: WindowData | DoorData | SwingDoorData, productLine: string, extCost: number): string {
  const sqIn = win.width * win.height;
  const sqFt = sqIn / 144;
  const basePrice = calculateBasePrice(win.width, win.height, productLine);
  const adderCost = getItemAdderCost(win, extCost);
  const unitPrice = basePrice + adderCost;
  const lineTotal = unitPrice * win.qty;
  const adderNames = Object.entries(win.adders).filter(([, v]) => v.checked).map(([name]) => name);
  if (extCost > 0) adderNames.push('Ext Color');
  if (!isSwingDoor(win) && hasCasement(win.operations as any)) adderNames.push('Casement');
  if ((win as any).tempered) adderNames.push('Tempered');
  const adders = adderNames.join('; ') || 'None';
  const ops = isSwingDoor(win) ? getSwingDoorOperationLabel(win) : win.operations && win.operations.length > 0 ? win.operations.join('; ') : 'None';
  const glass = win.glass || 'No';
  return `${win.qty},${win.width},${win.height},${sqFt.toFixed(1)},"${ops}","${glass}","${adders}",${adderCost.toFixed(2)},${lineTotal.toFixed(2)}`;
}
function buildSectionCsv(sectionLabel: string, items: (WindowData | DoorData | SwingDoorData)[], productLine: string, extCost: number): string[] {
  const included = items.filter(w => w.included);
  if (included.length === 0) return [];
  const rows: string[] = [];
  rows.push('');
  rows.push(sectionLabel);
  rows.push('Qty,Width (in),Height (in),Sq Ft,Operation,Obscured,Add-Ons,Adder Cost,Line Total');
  included.forEach(win => {
    rows.push(buildItemRowCsv(win, productLine, extCost));
  });
  return rows;
}
function buildCsv(props: ExportActionsProps): string {
  const {
    windowItems,
    doorItems,
    swingDoorItems,
    productLine,
    selectedFrameColor,
    selectedExteriorColor,
    totalBasePrice,
    totalAdderCost,
    grandTotal,
    windowDiscount,
    sgdDiscount,
    swingDoorDiscount,
    totalDiscountAmount,
    totalDue,
    totalWindowCount,
    avgPerWindow
  } = props;
  const rows: string[] = [];
  const colorStr = selectedExteriorColor !== 'None' ? `${selectedFrameColor} / ${selectedExteriorColor}` : selectedFrameColor;
  rows.push(`Quote — ${productLine} (${colorStr})`);
  rows.push(`Date,${new Date().toLocaleDateString('en-US')}`);
  const csvExtCost = props.exteriorColorCost;
  rows.push(...buildSectionCsv('Windows', windowItems, productLine, csvExtCost));
  rows.push(...buildSectionCsv('Sliding Glass Doors', doorItems, productLine, csvExtCost));
  rows.push(...buildSectionCsv('Swing Doors', swingDoorItems, productLine, csvExtCost));
  rows.push('');
  rows.push(`Total Items,${totalWindowCount}`);
  rows.push(`Base Total,${totalBasePrice.toFixed(2)}`);
  rows.push(`Adders Total,${totalAdderCost.toFixed(2)}`);
  rows.push(`Subtotal,${grandTotal.toFixed(2)}`);
  if (windowDiscount > 0) rows.push(`Windows Discount (${windowDiscount.toFixed(1)}%)`);
  if (sgdDiscount > 0) rows.push(`SGD Discount (${sgdDiscount.toFixed(1)}%)`);
  if (swingDoorDiscount > 0) rows.push(`Swing Door Discount (${swingDoorDiscount.toFixed(1)}%)`);
  if (totalDiscountAmount > 0) {
    rows.push(`Total Discount,-${totalDiscountAmount.toFixed(2)}`);
  }
  rows.push(`Total Due,${totalDue.toFixed(2)}`);
  rows.push(`Avg per Window,${avgPerWindow.toFixed(2)}`);
  return rows.join('\n');
}
export function ExportActions(props: ExportActionsProps) {
  const [copied, setCopied] = useState(false);
  const allItems = [...props.windowItems, ...props.doorItems, ...props.swingDoorItems];
  const missingOperations = allItems.some(w => {
    if (!w.included) return false;
    if (isSwingDoor(w)) return !isSwingDoorOperationComplete(w);
    return !w.operations || w.operations.length === 0;
  });
  const missingCasementHinge = props.windowItems.some(w => w.included && hasCasement(w.operations) && !hasHingeInNotes(w.notes || ''));
  const blockExport = missingOperations || missingCasementHinge;
  async function handleCopyPlainText() {
    if (blockExport) return;
    const plain = buildPlainText(props);
    try {
      await navigator.clipboard.writeText(plain);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = plain;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  }
  function handleDownloadCsv() {
    if (blockExport) return;
    const csv = buildCsv(props);
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `window-quote-${props.productLine.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  return <div className="flex flex-col gap-2" data-id="element-72">
      <div className="flex flex-wrap items-center gap-3" data-id="element-73">
        <button type="button" onClick={handleCopyPlainText} disabled={blockExport} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${blockExport ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#2E5E3F] hover:bg-[#244D33]'}`} data-id="element-74">
          {copied ? <>
              <CheckIcon className="w-4 h-4" data-id="element-75" />
              Copied!
            </> : <>
              <ClipboardCopyIcon className="w-4 h-4" data-id="element-76" />
              Copy Quote to Clipboard
            </>}
        </button>
        <button type="button" onClick={handleDownloadCsv} disabled={blockExport} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${blockExport ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed' : 'text-[#2E5E3F] bg-white border border-gray-300 hover:bg-gray-50'}`} data-id="element-77">
          <DownloadIcon className="w-4 h-4" data-id="element-78" />
          Download CSV
        </button>
        {copied && <span className="text-xs text-gray-500" data-id="element-79">
            Paste into Go High Level's text editor
          </span>}
      </div>
      {missingOperations && <p className="text-xs text-red-500 font-medium" data-id="element-80">
          * Operation is required on every included window before exporting.
        </p>}
      {missingCasementHinge && <p className="text-xs text-red-500 font-medium" data-id="element-81">
          * Casement windows require hinge side (Left or Right) in the Notes
          column before exporting.
        </p>}
    </div>;
}